import washingIcon from '@assets/atemporal/fabrics/washing.png'
import washingSymbolsIcon from '@assets/atemporal/fabrics/washing-symbols.png'
import solventIcon from '@assets/atemporal/fabrics/solvent.png'
import ironIcon from '@assets/atemporal/fabrics/iron.png'
import noBleachIcon from '@assets/atemporal/fabrics/no-iron.png'
import dryCleanPIcon from '@assets/atemporal/fabrics/dry-clean.png'
import dryCleanIcon from '@assets/atemporal/fabrics/clothes.png'
import doNotTumbleDryIcon from '@assets/atemporal/fabrics/do-not-tumble-dry.png'
import dripDryIcon from '@assets/atemporal/fabrics/drip-dry.png'
import laundryIcon from '@assets/atemporal/fabrics/laundry.png'
import noWringIcon from '@assets/atemporal/fabrics/no-wring.png'
import { isEnglishUi, pickLocalizedText, type LocalizedText } from '@utils/i18n/uiLocale'
import { ui } from '@utils/i18n/uiCopy'

export type FabricCareIconId =
  | 'wash30'
  | 'washingSymbols'
  | 'noBleach'
  | 'iron'
  | 'noTumbleDry'
  | 'dryClean'
  | 'dryCleanP'
  | 'dripDry'
  | 'noWring'
  | 'gentleWash'
  | 'noDryClean'

export type FabricCharacteristicsProfile = {
  id: string
  /** Nombre del grupo en TexturesPanel (p. ej. ALGODÓN). */
  group: string
  /** Nombre del subgrupo/familia en TexturesPanel. */
  title: string
  description: string
  icons: FabricCareIconId[]
}

const FABRIC_CARE_ICON_META: Record<
  FabricCareIconId,
  { src: string; alt: LocalizedText; sizeClass?: string; htmlSizeClass?: string }
> = {
  wash30: {
    src: washingIcon,
    alt: { es: 'Lavado a 30°C', en: 'Wash at 30°C' },
    sizeClass: 'h-9 w-9',
    htmlSizeClass: 'fabric-care-icon--medium',
  },
  washingSymbols: {
    src: washingSymbolsIcon,
    alt: { es: 'Símbolos de lavado', en: 'Washing symbols' },
    sizeClass: 'h-6 w-6',
    htmlSizeClass: 'fabric-care-icon--slightly-small',
  },
  noBleach: { src: noBleachIcon, alt: { es: 'No usar lejía', en: 'Do not bleach' } },
  iron: {
    src: ironIcon,
    alt: { es: 'Planchar a temperatura baja', en: 'Iron at low temperature' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--slightly-large',
  },
  noTumbleDry: { src: doNotTumbleDryIcon, alt: { es: 'No secar en secadora', en: 'Do not tumble dry' } },
  dryClean: {
    src: dryCleanIcon,
    alt: { es: 'Lavado en seco', en: 'Dry clean' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--slightly-large',
  },
  dryCleanP: { src: dryCleanPIcon, alt: { es: 'Lavado en seco (P)', en: 'Dry clean (P)' } },
  dripDry: { src: dripDryIcon, alt: { es: 'Secar en vertical', en: 'Drip dry' } },
  noWring: {
    src: noWringIcon,
    alt: { es: 'No retorcer', en: 'Do not wring' },
    sizeClass: 'h-8 w-8',
    htmlSizeClass: 'fabric-care-icon--large',
  },
  gentleWash: { src: laundryIcon, alt: { es: 'Lavado delicado', en: 'Gentle wash' } },
  noDryClean: {
    src: solventIcon,
    alt: { es: 'No lavar en seco', en: 'Do not dry clean' },
    sizeClass: 'h-6 w-6',
    htmlSizeClass: 'fabric-care-icon--slightly-small',
  },
}

const FABRIC_CHARACTERISTICS_PROFILES: Array<{
  id: string
  match: (key: string) => boolean
  group: LocalizedText
  title: LocalizedText
  description: LocalizedText
  icons: FabricCareIconId[]
}> = [
  {
    id: 'mild-cotton',
    match: (key) => key.includes('mildcotton'),
    group: { es: 'Algodón', en: 'Cotton' },
    title: { es: 'Mild Cotton', en: 'Mild Cotton' },
    description: { es: '100% Algodón · Ancho 140 cms · Martindale 21.000', en: '100% Cotton · Width 140 cm · Martindale 21,000' },
    icons: ['iron', 'dryCleanP'],
  },
  {
    id: '100-algodon-atemporal',
    match: (key) =>
      key.includes('100algodon') ||
      (key.includes('algodon') && key.includes('atemporal') && !key.includes('greatlinen')),
    group: { es: 'Algodón', en: 'Cotton' },
    title: { es: '100% Algodón Atemporal', en: '100% Cotton Atemporal' },
    description: { es: '100% Algodón · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Cotton · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dripDry', 'dryClean'],
  },
  {
    id: 'great-linen',
    match: (key) => key.includes('greatlinen'),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Great Linen Atemporal', en: 'Great Linen Atemporal' },
    description: { es: '100% Lino 750gr/m2 · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Linen 750gr/m2 · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
  },
  {
    id: 'carlota-soft',
    match: (key) => key.includes('carlota'),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Carlota SOFT', en: 'Carlota SOFT' },
    description: { es: '100% Lino · 750gr/m2 · Ancho 140 cms · Martindale 28.000 · Encogimiento 3/4%', en: '100% Linen · 750gr/m2 · Width 140 cm · Martindale 28,000 · Shrinkage 3/4%' },
    icons: ['wash30', 'noTumbleDry', 'iron', 'gentleWash', 'noWring', 'dryCleanP'],
  },
  {
    id: 'alessandra-easyclean',
    match: (key) => key.includes('alessandra'),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Alessandra EasyClean®', en: 'Alessandra EasyClean®' },
    description: { es: '100% Lino · 750gr/m2 · Ancho 140 cms · Martindale 22.500', en: '100% Linen · 750gr/m2 · Width 140 cm · Martindale 22,500' },
    icons: ['wash30', 'dripDry', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
  },
  {
    id: 'belgium',
    match: (key) => key.includes('belgium'),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Belgium', en: 'Belgium' },
    description: { es: '60% Lino 40% Algodón · 750gr/m2 · Ancho 140 cms · Martindale 25.000 · Encogimiento 0/1%', en: '60% Linen 40% Cotton · 750gr/m2 · Width 140 cm · Martindale 25,000 · Shrinkage 0/1%' },
    icons: ['dryCleanP'],
  },
  {
    id: 'linco-easyclean',
    match: (key) =>
      (key.includes('linco') || key.includes('lincoeasyclean') || key.includes('lincoeasywash')) &&
      !key.includes('linenperformance') &&
      !(key.includes('linen') && key.includes('performance')),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Linco EasyClean®', en: 'Linco EasyClean®' },
    description: { es: '63% Lino 37% Algodón · Ancho 134 cms · Martindale 32.500', en: '63% Linen 37% Cotton · Width 134 cm · Martindale 32,500' },
    icons: ['wash30', 'dripDry', 'noTumbleDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
  },
  {
    id: 'linen-performance-easyclean',
    match: (key) =>
      key.includes('linenperformance') || (key.includes('linen') && key.includes('performance')),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Linen Performance EasyClean®', en: 'Linen Performance EasyClean®' },
    description: { es: '93% Pes 7% Lino · Ancho 141 cms · Martindale 100.000', en: '93% Pes 7% Linen · Width 141 cm · Martindale 100,000' },
    icons: ['wash30', 'washingSymbols', 'iron', 'gentleWash', 'noDryClean'],
  },
  {
    id: 'clan',
    match: (key) => key.includes('clan'),
    group: { es: 'Lino', en: 'Linen' },
    title: { es: 'Clan', en: 'Clan' },
    description: { es: '63% Lino 37% Algodón · Ancho 136 cms · Martindale 32.500', en: '63% Linen 37% Cotton · Width 136 cm · Martindale 32,500' },
    icons: ['wash30', 'dripDry', 'iron', 'gentleWash', 'noDryClean', 'dryClean'],
  },
  {
    id: 'terra',
    match: (key) => key.includes('terra'),
    group: { es: 'Algodón', en: 'Cotton' },
    title: { es: 'Terra', en: 'Terra' },
    description: { es: '100% Pes · Ancho 138 cms · Martindale 100.000', en: '100% Pes · Width 138 cm · Martindale 100,000' },
    icons: ['wash30', 'washingSymbols', 'noBleach', 'gentleWash', 'noDryClean'],
  },
]

function normalizeFabricKey(value?: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
}

export function resolveFabricCharacteristics(
  acabado: string,
  fabricName = '',
): FabricCharacteristicsProfile | null {
  const key = normalizeFabricKey(`${acabado} ${fabricName}`)
  if (!key) return null

  const profile = FABRIC_CHARACTERISTICS_PROFILES.find((entry) => entry.match(key))
  if (!profile) return null

  return {
    id: profile.id,
    group: pickLocalizedText(profile.group),
    title: pickLocalizedText(profile.title),
    description: pickLocalizedText(profile.description),
    icons: profile.icons,
  }
}

export type FabricTextureInput = {
  acabado?: string | null
  name?: string | null
  description?: string | null
  descripcion?: string | null
  modelo?: string | null
  identificadorTextura?: number | string | null
  zoneName?: string | null
  zoneDisplayName?: string | null
  zone?: string | null
  url?: string | null
  urlMiniatura?: string | null
}

export type FabricPanelLabels = {
  group: string | null
  subgroup: string | null
  /** Código de color/variante del swatch, p. ej. "1007" */
  textureLabel: string | null
}

/** Limpia códigos y separadores: "2581_Mild Cotton" → "Mild Cotton" */
export function cleanFabricDisplayName(raw: string) {
  let text = String(raw || '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
  if (!text) return ''

  text = text.replace(/^\d{2,8}[_\s.\-]+/, '').trim()
  text = text.replace(/^[\s_=\-–—]+/u, '').trim()
  text = text.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim()

  const parts = text.split(/\s+/).filter(Boolean)
  if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1])) {
    text = parts.slice(0, -1).join(' ')
  }

  return text.replace(/^[\s_]+|[\s_]+$/g, '').trim()
}

/** "ALGODÓN" → "Algodón"; "MILD COTTON" → "Mild cotton".
 * Solo reescribe si el texto llega mayoritariamente en mayúsculas (dump de API);
 * respeta casing ya correcto ("Mild Cotton", "Algodón").
 */
export function toGroupTitleCase(raw: string) {
  const text = String(raw || '').trim()
  if (!text) return ''

  const letters = Array.from(text).filter((ch) => /\p{L}/u.test(ch))
  if (letters.length === 0) return text

  const upperCount = letters.filter(
    (ch) => ch === ch.toLocaleUpperCase('es') && ch !== ch.toLocaleLowerCase('es'),
  ).length
  const mostlyUpper = upperCount / letters.length >= 0.7
  if (!mostlyUpper) return text

  const lower = text.toLocaleLowerCase('es')
  return lower.charAt(0).toLocaleUpperCase('es') + lower.slice(1)
}

function isPlaceholderFinishLabel(value?: string | null): boolean {
  const key = normalizeFabricKey(String(value || ''))
  return !key || key === 'acabado' || key === 'unnamed' || key === 'sinombre'
}

function pickRealFinishValue(...values: unknown[]): string | null {
  const cleaned = values.map((value) => String(value || '').trim()).filter(Boolean)
  return cleaned.find((value) => !isPlaceholderFinishLabel(value)) || cleaned[0] || null
}

function shortenFabricFamilyDisplayName(raw: string): string {
  const key = normalizeFabricKey(raw)
  if (!key) return raw

  if (
    (key.includes('100algodon') && key.includes('atemporal')) ||
    (key.includes('cotton') && key.includes('atemporal') && !key.includes('greatlinen'))
  ) {
    return pickLocalizedText({ es: '100% Algodón Atemporal', en: '100% Cotton Atemporal' })
  }
  if (key.includes('greatlinen')) {
    return 'Great Linen Atemporal'
  }
  if (key.includes('belgium')) {
    return 'Belgium'
  }
  return raw
}

function humanizeFabricGroupSlug(raw: string): string {
  const key = normalizeFabricKey(raw)
  if (!key) return raw
  if (key === 'algodon' || /^\d+algodon$/.test(key)) {
    return pickLocalizedText({ es: 'Algodón', en: 'Cotton' })
  }
  if (key === 'lino' || /^\d+lino$/.test(key)) {
    return pickLocalizedText({ es: 'Lino', en: 'Linen' })
  }
  return raw
}

/**
 * Localiza nombres de tejido/acabado que vienen del back en español.
 * ES: se dejan como llegan (con title-case suave).
 * EN: usa perfil conocido si existe; si no, traduce términos habituales (Algodón→Cotton…).
 * Conserva códigos numéricos de textura al final (p. ej. "… EasyClean 1007").
 */
export function localizeFabricDisplayName(raw?: string): string {
  const value = String(raw || '').trim()
  if (!value) return ''

  const humanizedGroup = humanizeFabricGroupSlug(value)
  if (humanizedGroup !== value) return humanizedGroup

  const titled = toGroupTitleCase(value) || value
  const codeMatch = titled.match(/^(.*?)(\s+\d+)$/)
  const base = (codeMatch ? codeMatch[1] : titled).trim()
  const codeSuffix = codeMatch ? codeMatch[2] : ''
  const shortened = shortenFabricFamilyDisplayName(base)
  const displayBase = shortened !== base ? shortened : base

  if (!isEnglishUi()) return `${displayBase}${codeSuffix}`

  const profile = resolveFabricCharacteristics(base, base)
  if (profile?.title) return `${profile.title}${codeSuffix}`
  if (profile?.group && normalizeFabricKey(base) === normalizeFabricKey(profile.group)) {
    return `${profile.group}${codeSuffix}`
  }

  const translated = displayBase
    .replace(/\bAlgodones\b/gi, 'Cottons')
    .replace(/\bAlgodón\b/gi, 'Cotton')
    .replace(/\bAlgodon\b/gi, 'Cotton')
    .replace(/\bLinos\b/gi, 'Linens')
    .replace(/\bLino\b/gi, 'Linen')
    .replace(/\bAncho\b/gi, 'Width')
    .replace(/\bEncogimiento\b/gi, 'Shrinkage')
    .replace(/\bcms\b/gi, 'cm')

  return `${translated}${codeSuffix}`
}

/**
 * Código de textura como en TexturesPanel: último token numérico
 * ("Alessandra 13134" → "13134", "Carlota 01" → "01").
 */
export function extractFabricVariantCode(raw?: string): string | null {
  const text = String(raw || '').trim()
  if (!text) return null

  const tokens = text.split(/[\s_./\-]+/).filter(Boolean)
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (/^\d{1,6}$/.test(tokens[i])) return tokens[i]
  }
  return null
}

/**
 * Código de textura (el número de variante / color del swatch).
 * Prioriza el nombre aplicado (el de la miniatura) frente a una descripción antigua.
 */
export function resolveFabricTextureLabel(fabricTexture: FabricTextureInput | null) {
  if (!fabricTexture) return null

  const sources = [
    fabricTexture.name,
    fabricTexture.description,
    fabricTexture.descripcion,
    fabricTexture.modelo,
  ]
  for (const source of sources) {
    const code = extractFabricVariantCode(source)
    if (code) return code
  }

  const identificador = Number(fabricTexture.identificadorTextura)
  if (Number.isFinite(identificador) && identificador > 0 && identificador < 1_000_000) {
    return String(identificador)
  }
  return null
}

/** Une textura de escena + material de zona para no perder familia/código al aplicar. */
export function pickItemFabricTexture(item: {
  textures?: unknown
  zones?: Record<string, { material?: Record<string, unknown> }>
} | null): FabricTextureInput | null {
  const textures = Array.isArray(item?.textures) ? item.textures : []
  const fabricTexture =
    textures.find((texture: { zoneDisplayName?: string; zone?: string; zoneName?: string }) =>
      String(texture?.zoneDisplayName || texture?.zone || texture?.zoneName || '')
        .toLowerCase()
        .includes('tela'),
    ) || textures[0]
  if (!fabricTexture) return null

  const zoneName = String(fabricTexture.zoneName || '')
  const zoneMaterial = zoneName ? item?.zones?.[zoneName]?.material : null

  return {
    acabado: pickRealFinishValue(
      fabricTexture.acabado,
      zoneMaterial?.modelo,
      zoneMaterial?.acabado,
    ),
    name: (zoneMaterial?.name || fabricTexture.name || null) as string | null,
    description: (zoneMaterial?.description ||
      fabricTexture.description ||
      fabricTexture.descripcion ||
      null) as string | null,
    descripcion: (zoneMaterial?.descripcion || fabricTexture.descripcion || null) as string | null,
    modelo: (fabricTexture.modelo || zoneMaterial?.modelo || null) as string | null,
    identificadorTextura:
      zoneMaterial?.identificadorTextura ?? fabricTexture.identificadorTextura ?? null,
    zoneName: fabricTexture.zoneName || null,
    zoneDisplayName: fabricTexture.zoneDisplayName || null,
    zone: fabricTexture.zone || null,
    url: (zoneMaterial?.url || fabricTexture.url || null) as string | null,
    urlMiniatura: (zoneMaterial?.urlMiniatura || fabricTexture.urlMiniatura || null) as
      | string
      | null,
  }
}

/**
 * Grupo + subgrupo (+ textura) alineados con TexturesPanel / SelectedUnitInfo.
 */
export function resolveFabricPanelLabels(
  fabricTexture: FabricTextureInput | null,
): FabricPanelLabels {
  if (!fabricTexture) return { group: null, subgroup: null, textureLabel: null }

  const acabadoRaw = String(fabricTexture.acabado || '').trim()
  const acabadoClean = cleanFabricDisplayName(acabadoRaw)
  const rawCandidates = [
    fabricTexture.description,
    fabricTexture.descripcion,
    fabricTexture.name,
    isPlaceholderFinishLabel(fabricTexture.modelo) ? '' : fabricTexture.modelo,
    isPlaceholderFinishLabel(acabadoRaw) ? '' : acabadoRaw,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  const cleanedCandidates = rawCandidates.map(cleanFabricDisplayName).filter(Boolean)
  const searchBlob = [...rawCandidates, ...cleanedCandidates].join(' ')
  const profile = resolveFabricCharacteristics(acabadoRaw, searchBlob)

  const subgroupCandidate =
    profile?.title ||
    [...cleanedCandidates].sort((a, b) => {
      const score = (value: string) => {
        let s = 0
        if (/[%®]/.test(value)) s += 4
        if (/easyclean|easywash|atemporal|soft/i.test(value)) s += 3
        if (/\s/.test(value)) s += 2
        if (value.length > 8) s += 1
        if (/^[a-z0-9]+$/i.test(value)) s -= 3
        return s
      }
      return score(b) - score(a)
    })[0] ||
    null

  const subgroup = subgroupCandidate
    ? localizeFabricDisplayName(subgroupCandidate) || null
    : null

  const acabadoLooksLikeGroup =
    !!acabadoClean &&
    !isPlaceholderFinishLabel(acabadoClean) &&
    !/^\d/.test(acabadoClean) &&
    !/^\d+$/.test(acabadoClean) &&
    acabadoClean.length <= 32 &&
    !/easyclean|easywash|mild cotton|great linen|carlota|alessandra|linco|belgium|clan|terra|performance/i.test(
      acabadoClean,
    )

  // La familia del panel (Algodón / Lino) manda; slugs tipo "100Algodon" se humanizan.
  // Nunca mostrar el placeholder de API "Acabado".
  let group =
    localizeFabricDisplayName(
      (acabadoLooksLikeGroup ? acabadoClean : '') ||
        profile?.group ||
        (isPlaceholderFinishLabel(acabadoClean) ? '' : acabadoClean) ||
        '',
    ) || null
  if (isPlaceholderFinishLabel(group)) group = null

  if (group && subgroup) {
    if (normalizeFabricKey(group) === normalizeFabricKey(subgroup)) group = null
  }

  return {
    group,
    subgroup,
    textureLabel: resolveFabricTextureLabel(fabricTexture),
  }
}

export function getFabricCareIconMeta(iconId: FabricCareIconId) {
  const icon = FABRIC_CARE_ICON_META[iconId]
  return { ...icon, alt: pickLocalizedText(icon.alt) }
}

export function getFabricCareIconSrc(iconId: FabricCareIconId): string {
  return FABRIC_CARE_ICON_META[iconId]?.src || ''
}

export function getFabricCareIconClassName(iconId: FabricCareIconId): string {
  return FABRIC_CARE_ICON_META[iconId]?.sizeClass || 'h-7 w-7'
}

export function getFabricCareIconHtmlClassName(iconId: FabricCareIconId): string {
  const extra = FABRIC_CARE_ICON_META[iconId]?.htmlSizeClass
  return extra ? `fabric-care-icon ${extra}` : 'fabric-care-icon'
}

async function moduleUrlToDataUrl(url: string): Promise<string> {
  if (!url) return ''
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '')
      }
      reader.onerror = () => reject(new Error('read'))
      reader.readAsDataURL(blob)
    })
  } catch {
    return ''
  }
}

export async function fetchFabricCareIconsAsDataUrls(
  profile: FabricCharacteristicsProfile | null,
): Promise<Partial<Record<FabricCareIconId, string>>> {
  if (!profile) return {}

  const uniqueIcons = Array.from(new Set(profile.icons))
  const entries = await Promise.all(
    uniqueIcons.map(async (iconId) => {
      const src = getFabricCareIconSrc(iconId)
      const dataUrl = await moduleUrlToDataUrl(src)
      return [iconId, dataUrl] as const
    }),
  )

  return Object.fromEntries(entries.filter(([, dataUrl]) => Boolean(dataUrl)))
}

export function buildFabricCharacteristicsHtml(
  escapeHtml: (value: string) => string,
  profile: FabricCharacteristicsProfile,
  iconDataUrls: Partial<Record<FabricCareIconId, string>>,
): string {
  const iconsHtml = profile.icons
    .map((iconId) => {
      const src = iconDataUrls[iconId] || getFabricCareIconSrc(iconId)
      const alt = escapeHtml(getFabricCareIconMeta(iconId).alt)
      if (!src) return ''
      return `<img class="${getFabricCareIconHtmlClassName(iconId)}" src="${src}" alt="${alt}" />`
    })
    .filter(Boolean)
    .join('')

  return `<div class="fabric-care-block">
    <div class="fabric-care-title"><span class="label-strong">${escapeHtml(ui('fabricCharacteristics'))}</span></div>
    <div class="fabric-care-group">${escapeHtml(profile.title)}</div>
    <div class="fabric-care-desc">${escapeHtml(profile.description)}</div>
    <div class="fabric-care-icons">${iconsHtml}</div>
  </div>`
}
